import { FilterKeys } from '../../../services/utils'
import { algoliaBaseHelper } from './algoliaBaseHelper'
import { mapGetters } from 'vuex'

export const algoliaProductHelper = {
  mixins: [algoliaBaseHelper],
  data() {
    return {
      defaultProductFacets:
        '*,active,visible,' +
        'brand.values,brand.is_published,brand.minimum_order_value,brand.based_in,brand.id,' +
        'categories.id,lead_time_from,pos_price,wholesale_price,characteristic_options.color,' +
        'characteristic_options.size'
    }
  },
  beforeMount: function () {
    this.searchIndex = this.setIndex()
  },
  computed: {
    ...mapGetters({
      favoriteList: 'favoriteProductsState'
    })
  },
  watch: {
    favoriteList: function () {
      this.products = this.applyFavorites(this.products)
    }
  },
  methods: {
    setIndex: function (index = '_products') {
      if (index !== '_products') {
        index = '_products_' + index
      }
      return this.algoliaClient.initIndex(this.$env.getOrigin() + index)
    },
    getProductsOnSearch(params, pageLevelFilters, limit, eventId) {
      const { keyword, sortBy, page } = params || {}

      const distinct = this.setDistinct(params, pageLevelFilters)
      this.pagination.limit = distinct === 3 ? 12 : limit || this.pagination.limit

      this.loading = true
      this.searchIndex = this.setIndex(this.normalizeSortByValue(sortBy))
      this.searchIndex
        .search(keyword || '', {
          clickAnalytics: true,
          page: (page || 1) - 1,
          hitsPerPage: this.pagination.limit,
          distinct,
          facets: this.defaultProductFacets,
          facetFilters: this.getFacetFiltersFromParams(params, pageLevelFilters),
          numericFilters: this.getNumericFiltersFromParams(params)
        })
        .then((results) => {
          this.products = this.applyFavorites(results.hits, eventId)
          this.$trackingStore.commit('setQueryId', this.$route.name === 'search' ? results.queryID : null)
          if (distinct && distinct > 1) {
            this.pagination.totalCount = Object.keys(results.facets['brand.id'])
              .map((key) => results.facets['brand.id'][key])
              .reduce((result, value) => {
                const valueToAdd = value > distinct ? distinct : value
                result += valueToAdd
                return result
              }, 0)
          } else {
            this.pagination.totalCount = results.nbHits
          }

          this.pagination.numberOfPages = results.nbPages

          if (this.$route.path.includes('search')) {
            this.trackSearchEvent(results.query, results.nbHits)
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    getBrandsHaveProductInCategory(sortBy, pageLevelFilters, limit) {
      const distinct = this.setDistinct(sortBy, pageLevelFilters)
      this.pagination.limit = distinct === 3 ? 12 : limit || this.pagination.limit
      this.searchIndex = this.setIndex(this.normalizeSortByValue(sortBy))
      this.searchIndex
        .search('', {
          page: 0,
          hitsPerPage: this.pagination.limit,
          distinct,
          facets: this.defaultProductFacets,
          facetFilters: this.getFacetFiltersFromParams({}, pageLevelFilters),
          numericFilters: this.getNumericFiltersFromParams({})
        })
        .then((results) => {
          const categoryBrandIds = Object.keys(results?.facets['brand.id']) || []
          this.$store.commit('filters/setCategoryBrandsIds', categoryBrandIds)
        })
    },
    getBrandsHaveProductWithSearchTerm(keyword) {
      this.searchIndex = this.setIndex(this.$utils.constants.DEFAULT_PRODUCT_SORTING_OPTIONS[0].value)
      this.searchIndex
        .search(keyword || '', {
          page: 0,
          hitsPerPage: this.pagination.limit,
          facets: this.defaultProductFacets,
          facetFilters: this.getFacetFiltersFromParams({}),
          numericFilters: this.getNumericFiltersFromParams({})
        })
        .then((results) => {
          if (results?.hits?.length) {
            const brandIds = Object.keys(results?.facets['brand.id']) || []
            this.$store.commit('filters/setSearchTermBrandIds', { keyword, brandIds })
          } else {
            this.$store.commit('filters/setSearchTermBrandIds', { keyword, brandIds: [] })
          }
        })
    },
    applyFavorites: function (items, eventId) {
      return items
        ?.filter((item) => {
          const brand = item.brand
          if (eventId === undefined) return true
          return (
            (brand.event_ids?.includes(eventId) && brand.source === 'EXHIBITOR') ||
            ['APPLICATION', 'OUTREACH'].includes(brand.source) ||
            brand.buy_on_bulletin
          )
        })
        .map((item) => ({
          ...item,
          ...{ is_fav: this.favoriteList?.includes(item.id) }
        }))
    },
    getFacetFiltersFromParams(params, pageLevelFilters) {
      const { brands, brand_values, categories, location, bulletin_picks, buy_on_bulletin } = params

      const { pageLevelCategoryFilter, brandId } = pageLevelFilters || {}

      let filters = [
        ...(this.product_ids ? [this.product_ids.map((id) => `id:${id}`)] : []),
        ...this.$utils.constants.ALGOLIA_DEFAULT_PRODUCT_SEARCH_FACET_FILTER,
        ...(this.$route.name === 'brand-bio' ? [] : ['visible:true']),
        ...[this.$utils.arrayify(brands).map((brand) => `brand.id:${brand}`)],
        ...[this.$utils.arrayify(brand_values).map((brandValue) => `brand.values: ${brandValue}`)],
        ...[this.$utils.arrayify(categories).map((category) => `categories.id:${category}`)],
        ...[this.$utils.arrayify(location).map((state) => `brand.address.state:${state}`)]
      ]

      if (pageLevelCategoryFilter) {
        filters = [
          ...filters,
          ...[
            pageLevelCategoryFilter?.isParent
              ? `categories.parent_category:${pageLevelCategoryFilter.value}`
              : `categories.id:${pageLevelCategoryFilter.value}`
          ]
        ]
      }

      if (brandId) {
        filters = [...filters, ...[`brand.id:${brandId}`]]
      }

      if (bulletin_picks) {
        filters = [...filters, ['brand.bulletin_pick:true']]
      }

      if (buy_on_bulletin) {
        filters = [...filters, ['brand.buy_on_bulletin:true']]
      }

      return filters
    },
    getNumericFiltersFromParams(params) {
      const { price_from, price_to, mov, lead_time_to } = params
      return [
        price_from ? `pos_price >= ${price_from}` : [],
        price_to ? `pos_price <= ${price_to}` : [],
        mov ? 'brand.minimum_order_value<=' + mov : [],
        lead_time_to ? `lead_time_from <=${lead_time_to}` : []
      ]
    },
    setDistinct(params, pageLevelFilters) {
      if (pageLevelFilters?.brandId) {
        return false
      }

      const appliedFilters = Object.keys(params).filter(
        (param) =>
          param !== FilterKeys.CATEGORIES.toString() &&
          Object.keys(FilterKeys).find((key) => {
            const isFilterAccepted = FilterKeys[key].toString() === param && param !== 'keyword'
            const value = params[param]
            let isFilterActive = false
            if (isFilterAccepted) {
              if (Array.isArray(value)) {
                isFilterActive = value?.length
              } else {
                isFilterActive = !!value
              }
            }
            return isFilterAccepted && isFilterActive
          })
      )

      if (appliedFilters?.length) {
        return false
      }

      return params.sortBy && params.distinct ? 3 : false
    },
    async getFavoriteProducts() {
      if (this.$session.getUserInfo()?.role !== 'RETAILER') {
        return
      }

      await this.$utils.getFavoriteProducts.call(this)
    },
    trackSearchEvent(query, hits) {
      this.$trackEvent({
        action: 'View Search Results',
        properties: {
          'search term': query,
          'number of results': hits
        }
      })
    },
    normalizeSortByValue(sortBy) {
      return sortBy?.includes('_distinct') ? sortBy.split('_distinct')[0] : sortBy
    }
  }
}
